import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CeIconComponent } from '@enginuity/core/atoms/ce-icon/ce-icon.component';
import { MenuSettings } from '@services/setting-services/models/menu-settings.enum';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { OverlayModule } from '@angular/cdk/overlay';
import { CeShortcutTooltipDirective } from '../../../shared/tooltip/shortcut-tooltip.directive';
import {
  NAVIGATION_SHORTCUTS,
  ShortcutsUtils,
  type NavigationInfo,
} from '@services/core-services/shortcuts.constants';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    CommonModule,
    CeIconComponent,
    NgOptimizedImage,
    RouterModule,
    MatTooltipModule,
    MatMenuModule,
    OverlayModule,
    CeShortcutTooltipDirective,
  ],
})
export class SideNavComponent implements OnInit {
  store_slug: string = '';
  isSubmenuOpen = false;

  route = inject(ActivatedRoute);

  menuSettings = MenuSettings;
  @Input()
  settings: MenuSettings.OrganisationSetting | MenuSettings.StoreSetting =
    MenuSettings.OrganisationSetting;

  ngOnInit() {
    this.store_slug = this.route.snapshot.paramMap.get('store_slug') || 'default-slug';
  }

  getShortcutRoute(name: keyof typeof NAVIGATION_SHORTCUTS) {
    const route = NAVIGATION_SHORTCUTS[name]?.route ?? '';
    return this.computeRoute(route);
  }

  getShortcutInfo(name: keyof typeof NAVIGATION_SHORTCUTS): NavigationInfo {
    const base = ShortcutsUtils.getNavigationInfo(name);
    return { ...base, route: this.computeRoute(base.route) };
  }

  appInitializer() {
    return () => {
      document.body.classList.add('app-loaded'); // Add your class here
    };
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const keyCode = event.key;
    const target = event.target as HTMLElement | null;
    // Ignore global arrow handling when focus is inside a submenu container
    if (target && (target.closest('.hover-menu') || target.closest('.side-nav-list'))) {
      return;
    }

    if (keyCode === 'ArrowUp') {
      this.navigateUp();
    } else if (keyCode === 'ArrowDown') {
      this.navigateDown();
    }
  }

  navigateUp() {
    const menuItems = document.querySelectorAll('.menu-item');
    const currentIndex = Array.from(menuItems).findIndex(item => item.classList.contains('active'));

    if (currentIndex > 0) {
      const previousMenuItem = menuItems[currentIndex - 1] as HTMLElement;
      previousMenuItem.focus();
    }
  }

  navigateDown() {
    const menuItems = document.querySelectorAll('.menu-item');
    const currentIndex = Array.from(menuItems).findIndex(item => item.classList.contains('active'));

    if (currentIndex < menuItems.length - 1) {
      const nextMenuItem = menuItems[currentIndex + 1] as HTMLElement;
      nextMenuItem.focus();
    }
  }

  onParentMenuKeydown(event: KeyboardEvent) {
    const key = event.key;
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    if (key === 'Enter' || key === ' ' || key === 'ArrowRight') {
      event.preventDefault();
      this.focusFirstSubmenuItem(target);
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      this.focusSiblingTrigger(target, key === 'ArrowRight' ? 1 : -1);
      return;
    }
  }

  onSubmenuKeydown(event: KeyboardEvent) {
    const key = event.key;
    const item = event.currentTarget as HTMLElement | null;
    if (!item) return;

    const submenu = item.closest('ul');
    if (!submenu) return;
    const items = Array.from(
      submenu.querySelectorAll<HTMLElement>('a.cursor-pointer, a[role="menuitem"]')
    );
    const index = items.indexOf(item);

    if (key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      const next = items[(index + 1) % items.length];
      next?.focus();
      return;
    }
    if (key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      const prev = items[(index - 1 + items.length) % items.length];
      prev?.focus();
      return;
    }
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      item.click();
      item.blur();
      const parentTrigger = submenu
        .closest('li.menu-list')
        ?.querySelector<HTMLElement>('a.menu-link');
      parentTrigger?.blur();
      return;
    }
    if (key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      item.blur();
      const parentTrigger = submenu
        .closest('li.menu-list')
        ?.querySelector<HTMLElement>('a.menu-link');
      parentTrigger?.blur();
      return;
    }
  }

  onParentFocus(event: FocusEvent) {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    target.classList.add('submenu-open');
    this.isSubmenuOpen = true;
  }

  onParentBlur(event: FocusEvent) {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    target.classList.remove('submenu-open');
    this.isSubmenuOpen = false;
  }

  onSubmenuClick(event: MouseEvent) {
    const item = event.currentTarget as HTMLElement | null;
    if (!item) return;
    const parentLi = item.closest('li.menu-list');
    const parentTrigger = parentLi?.querySelector<HTMLElement>('a.menu-link');
    parentTrigger?.classList.remove('submenu-open');
    parentLi?.classList.add('disable-hover-open');
    (document.activeElement as HTMLElement | null)?.blur?.();
    parentTrigger?.blur();
    setTimeout(() => parentLi?.classList.remove('disable-hover-open'), 250);
  }

  private focusFirstSubmenuItem(trigger: HTMLElement) {
    const submenu = trigger.closest('li.menu-list')?.querySelector('ul.hover-menu');
    const first = submenu?.querySelector<HTMLElement>('a.cursor-pointer, a[role="menuitem"]');
    first?.focus();
  }

  private focusSiblingTrigger(trigger: HTMLElement, delta: number) {
    const container = trigger.closest('ul.sideMenu');
    if (!container) return;
    const triggers = Array.from(container.querySelectorAll<HTMLElement>('a.menu-link[tabindex]'));
    const index = triggers.indexOf(trigger);
    if (index === -1) return;
    const next = triggers[(index + delta + triggers.length) % triggers.length];
    next?.focus();
  }

  private computeRoute(route: string): string {
    const clean = route.startsWith('/') ? route.slice(1) : route;
    if (this.settings === MenuSettings.StoreSetting && this.store_slug) {
      return `/store/${this.store_slug}/${clean}`;
    }
    return `/${clean}`;
  }
}
